import { ActionAPI, DropDownMenuAPI, FetchAPI, FieldListAPI, ModalButton, ModalProps, ModalService, PanelAPI, PDFViewerAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterFetch, OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HistoryParameters, HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Inject } from '@odinjs/odin';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import PacsLinkGeneratorService from '../../services/pacs-link-generator-service';
import ReportService from '../../services/report-service';
import BaseController from '../BaseController';
import { Data } from './../../interfaces/data';
import './exam-details-controller.css';
import {CardExamsEstablishmentInformationApi} from '@philips-emr/patient-components/src';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import UserContext from '../../utils/user-context';
import { EXAM_STATUS } from '../../constants/exam-status';
import { ModalButtonType } from '@philips-internal/oswald-ui-constants';
import { ApplicationModulesIds } from '../../app/module-registry/application-modules';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { Value } from '@philips-internal/oswald-ui-core/lib/types';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import moment from 'moment';
import { ExamDetailsAliases } from '../../codegen';

interface EstablishmentExam {
  cdCep: string;
  dsBarrio: string;
  dsEmail: string;
  dsEndereco: string;
  dsEstado: string;
  dsMunicipio: string;
  dsRazaoSocial: string;
  nrDddTelefone: string;
  nrDdiTelefone: string;
  nrEndereco: string;
  nrTelefone: string;
}

interface ExamDetail {
  cardOnSidebar?: boolean;
  cdEstabelecimento: string;
  dtAprovacao?: string;
  dtProcedimento: string;
  establishmentExam: EstablishmentExam;
  ieFormato: string;
  ieReview: string;
  internSequenceNumber: string;
  nmExame: string;
  nmPessoaFisica: string;
  nrAcessoDicom: string;
  nrPrescricao: string;
  nrSequencia: string;
  review?: boolean;
  sequenceExam: string;
  sequencePrescription: string;
  status: string;
  type: string;
}

interface CurrentRouteParams extends HistoryParameters {
  filterExamStatus: string;
  filterExamType: string;
  filterFromDate: string;
}

enum ExamType {
  LABORATORY = 'L',
  NON_LABORATORY = 'N',
  PATHOLOGY = 'P'
}
@Controller({ component: 'layout-exam-details', fragment: 'exam-details' })
class ExamDetails extends BaseController {

  @Inject('fetch-exam-details')
    fetchExamDetails!: FetchAPI

  @Inject('pdf-viewer-exam-details')
    pdfViewer!: PDFViewerAPI

  @Inject('card-exams-establishment-information-0')
    cardEstablishment!: CardExamsEstablishmentInformationApi;

  @Inject('fetch-exams')
    fetchExamsApi!: FetchAPI;

  @Inject('panel-exam-card')
    panelDetails!: PanelAPI;

  @Inject('panel-loader')
    panelLoader!: PanelAPI;

  @Inject('panel-pacs')
    panelPacs!: PanelAPI;

  @Inject('panel-message')
    panelReportError!: PanelAPI;

  @Inject('button-email')
    downloadButton!: ActionAPI;

  @Inject('panel-content-dropdown-send-mail')
    panelContentDropdownSendMail!: PanelAPI;

  @Inject('panel-loader-send-mail')
    panelLoaderSendMail!: PanelAPI;

  @Inject('button-email')
    emailButton!: ActionAPI;

  @Inject('field-list-dropdown-send-mail')
    fieldListDropdownSendMail!: FieldListAPI;

  @Inject('drop-down-menu-send-mail')
    dropDownMenuSendMail!: DropDownMenuAPI;

  @Inject('send-mail-field-notes')
    sendMailFieldNotes!: TextAreaAPI;

  @Inject('panel-loader-establishment')
    panelEstablishmentLoader!: PanelAPI;

  @Inject('panel-loader-details-1')
    panelDetailsLoaderOne!: PanelAPI;

  @Inject('panel-loader-details-2')
    panelDetailsLoaderTwo!: PanelAPI;

  @Inject('panel-exam-title')
    panelExamTitle!: PanelAPI;

  @Inject('panel-exam-left-card')
    panelExamInfo!: PanelAPI;

  @Inject('panel-exam-right-card')
    panelEstablishmentInfo!: PanelAPI;

  @Inject('panel-loader-report')
    panelReportLoader!: PanelAPI;

  @Inject('panel-tag-review')
    panelTagReview!: PanelAPI;

  @Inject(ExamDetailsAliases.PAC_BTN)
    viewImageButton!: ActionAPI;

  pacsLinkGeneratorService: PacsLinkGeneratorService = new PacsLinkGeneratorService();

  @OnMount({ target: 'layout-exam-details'})
  onLayoutExamsDetail(): void {
    const event = new CustomEvent('updateModulesRoute', { detail: { moduleId: ApplicationModulesIds.EXAMS } });
    dispatchEvent(event);
  }

  @OnUnmount(ExamDetailsAliases.PDF_VIEWER_EXAM_DETAILS)
  onUnMountPage(): void {
    this.clearPdfViewerPersistedState();
  }

  private clearPdfViewerPersistedState(): void {
    this.pdfViewer.clearPersistedState();
  }

  @OnMount({ target: 'fetch-exam-details' })
  onFilterMout(event: MountEvent): void {
    if (event) {
      this.panelReportError.setVisible(false);
      this.showDetailsInfo(false);
      this.showEstablishmentInfo(false);
      this.filterListParams();
      this.prepareFetch();
    }
  }

  @OnMount({ target: 'panel-pacs' })
  onPanelPacsMount(event: MountEvent): void {
    if (event) {
      const { examType } = this.historyService.getCurrentRouteQueryParams();
      this.panelPacs.setVisible(examType === 'N');
    }
  }

  @OnAfterFetch({ target: ['fetch-exam-details'] })
  onAfterExamDetailFetch(event: FetchEvent): void {
    if (event.data) {
      const data = event.data as ExamDetail;
      this.context.set({ nrAcessoDicom: data.nrAcessoDicom })
      this.loadReport(data);
      this.showEstablishmentInfo(true);
      this.showDetailsInfo(true);

      const showReviewTag = data.ieReview && data.ieReview === 'S';

      if (showReviewTag) {
        this.panelTagReview.setVisible(true);
      }

      if (data.status === EXAM_STATUS.AVAILABLE) {
        HttpService.post(APPLICATION_ENDPOINTS.POST_UPDATE_VIEW_EXAM, {
          ieTipoItem: data.type,
          nrPrescricao: data.nrPrescricao,
          nrSequencia: data.nrSequencia,
          patientId: UserContext.patientId
        });
      }
    }
  }

  @OnAfterFetch({ target: 'fetch-exams' })
  onAfterFetchExams(event: FetchEvent): void {
    const fetchData = event.data as ExamDetail[];

    fetchData.forEach(exam => {
      exam.review = exam.ieReview && exam.ieReview === 'S' ? true : false;
      exam.cardOnSidebar = true;
    });
  }

  @OnClick({ target: ['deck-exams'] })
  onCardDeckClick(event: ClickEvent): void {
    if (event && event.value) {
      this.showDetailsInfo(false);
      this.showEstablishmentInfo(false);
      this.pdfViewer.setVisible(false);
      this.loadExamPage(event);
    }
  }

  @OnClick({ target: ['download-exam-btn', 'menu-option-download-report'] })
  onDownloadBtnClick(event: ClickEvent): void {
    if (event) {
      this.downloadReport();
    }
  }

  @OnClick({ target: ['pac-btn', 'menu-option-pacs'] })
  OnClickPacBtn(): void {
    const { patientId } = UserContext;
    const nrAcessoDicom = this.context.get('nrAcessoDicom');
    const reportParams = this.context.get('reportParams') as Data;
    const organizationId = reportParams.CD_ESTABELECIMENTO || '';

    const callPacsInChrome = (): void => {
      const windowReference = window.open();
      this.pacsLinkGeneratorService.getPacsState().then(pacsState => {
        if (!pacsState.enabled) {
          alert(MessageService.translate('patient.no-pacs-available'));
          return;
        }

        this.pacsLinkGeneratorService.openPacsInChrome({
          examId: nrAcessoDicom?.toString() || '',
          organizationId: organizationId.toString(),
          patientId: patientId.toString(),
        }, windowReference)
      });
    }

    const dialogButtons: ModalButton[] = [
      {
        action: () => undefined,
        title: 'patient.cancel',
        type: ModalButtonType.CANCEL,
      },
      {
        action: callPacsInChrome,
        title: 'patient.continue',
        type: ModalButtonType.SUCCESS,
      }
    ];

    const dialogProps: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      dialogDescription: 'patient.pacs.open-new-tab',
      dockButtons: dialogButtons,
      focusOnOpen: true,
      title: 'patient.exam-details',
      uuid: generateUUID(),
    };

    ModalService.openDialog(dialogProps);
  }

  @OnClick({ target: ['button-email'] })
  onClickMailSend(event: ClickEvent): void {
    if (event) {
      const onModalClose = (): void => {
        return;
      };
      const mailParams = {
        emailType: 'report',
        params: this.context.get('reportParams') as Data
      }
      ModalService.openDefault({
        closeable: true,
        contextData: mailParams,
        dockButtons: [],
        fragment: 'send-mail',
        onClose: onModalClose,
        title: 'patient.send-by-email',
      });
    }
  }

  loadExamPage(event: ClickEvent): void {
    const { filterFromDate, filterExamStatus, filterExamType } = this.historyService.getCurrentRouteQueryParams() as CurrentRouteParams;
    const { nrSequencia, nrPrescricao, type, dtProcedimento, cdMedico } = event.value;
    const { patientId } = UserContext
    this.clearPdfViewerPersistedState();
    if (type === 'L') {
      // TODO: Refactor to not use query parameters and don't forget to adjust the "pushRouteByOpenModule" method of the "dashboard" fragment and the "pushToUrl" method of the "patient-root" fragment.
      this.historyService.push(APPLICATION_ROUTES.EXAM_LAB_DETAILS, {}, {
        searchParams: {
          cdMedico: cdMedico as string,
          dtProcedimento: dtProcedimento as string,
          filterExamStatus,
          filterExamType,
          filterFromDate,
          patientId: patientId,
        }
      });
    } else {
      // TODO: Refactor to not use query parameters and don't forget to adjust the "pushRouteByOpenModule" method of the "dashboard" fragment and the "pushToUrl" method of the "patient-root" fragment.
      this.historyService.push(APPLICATION_ROUTES.EXAM_DETAILS, {}, {
        searchParams: {
          examType: type as string,
          filterExamStatus,
          filterExamType,
          filterFromDate,
          nrPrescricao: nrPrescricao as string,
          nrSequencia: nrSequencia as string,
          patientId: patientId,
        }
      });
      this.fetchExamDetails.setParameters({
        examType: type,
        nrPrescricao,
        nrSequencia,
        patientId,
      });
      this.fetchExamDetails.setEndpoint(APPLICATION_ENDPOINTS.GET_EXAM_DETAILS);
      this.fetchExamDetails.forceFetch();
    }
  }

  prepareFetch(): void {
    const { nrSequencia, nrPrescricao, examType } = this.historyService.getCurrentRouteQueryParams();
    const { patientId } = UserContext;
    this.context.set({
      nrSequencia: nrSequencia as Value,
      patientId,
    })
    this.fetchExamDetails.setParameters({
      examType: examType as Value,
      nrPrescricao: nrPrescricao as Value,
      nrSequencia: nrSequencia as Value,
      patientId,
    });
    this.fetchExamDetails.setEndpoint(APPLICATION_ENDPOINTS.GET_EXAM_DETAILS);
    this.fetchExamDetails.forceFetch();
  }

  filterListParams(): void {
    const { filterFromDate, filterExamStatus, filterExamType } = this.historyService.getCurrentRouteQueryParams() as CurrentRouteParams;
    const { patientId } = UserContext;
    const dateFormat = moment(filterFromDate, ISO_DATE_FORMAT).format(ISO_DATE_FORMAT);
    this.fetchExamsApi.setParameters({
      dateFormat,
      filterExamStatus,
      filterExamType,
      patientId,
    });
    this.fetchExamsApi.forceFetch();
  }

  downloadReport(): void {
    if (this.pdfViewer.getSrc() != null) {
      const reportService = new ReportService(this);
      reportService.openInNewWindow(this.pdfViewer.getSrc());
    }
  }

  loadReport(data: ExamDetail): void {
    this.panelReportError.setVisible(false);
    if (data.status === EXAM_STATUS.READY || data.status === EXAM_STATUS.AVAILABLE) {
      const params = {
        CD_ESTABELECIMENTO: data.cdEstabelecimento,
        EXAM_NAME: data.nmExame,
        EXAM_TYPE: data.type,
        IE_FORMATO: data.ieFormato || 0,
        NR_ACESSO_DICOM: data.nrAcessoDicom || 0,
        NR_PRESCRICAO: data.nrPrescricao,
        NR_SEQ_INTERNOS: data.internSequenceNumber,
        NR_SEQ_PRESCR: data.sequencePrescription,
        NR_SEQUENCIA_P: data.nrSequencia,
        NR_SEQUENCIAS: data.sequenceExam,
        VL_PARAM_STATUS_EXAME: 35,
      }
      this.context.set({ reportParams: params });
      const reportService = new ReportService(this);
      this.panelReportLoader.setVisible(true);
      reportService.gerarRelatorio(params).then(file => {
        this.pdfViewer.setSrc(file);
        this.pdfViewer.setVisible(true);
        this.showReportOptions(true, data.type);
        this.panelReportLoader.setVisible(false);
      }).catch(() => {
        this.panelReportError.setVisible(true);
        this.showReportOptions(false);
        this.panelReportLoader.setVisible(false);
      });
    } else {
      this.pdfViewer.setVisible(false);
      this.showReportOptions(false);
      this.panelReportLoader.setVisible(false);
    }
  }

  showReportOptions(show: boolean, examType = ''): void {
    this.pdfViewer.setVisible(show);
    this.emailButton.setEnabled(show);
    this.downloadButton.setEnabled(show);
    this.viewImageButton.setEnabled(show && examType === ExamType.NON_LABORATORY);
  }

  showDetailsInfo(show: boolean): void {
    this.panelDetailsLoaderOne.setVisible(!show);
    this.panelExamTitle.setVisible(show);
    this.panelDetailsLoaderTwo.setVisible(!show);
    this.panelExamInfo.setVisible(show);
  }

  showEstablishmentInfo(show: boolean): void {
    this.panelEstablishmentLoader.setVisible(!show);
    this.panelEstablishmentInfo.setVisible(show);
  }
}

export default ExamDetails;
